import React from 'react'
import {
  Button,
  DropdownProps,
  Form,
  Icon,
  InputOnChangeData,
  Loader,
  Message,
  TextAreaProps,
} from 'semantic-ui-react'

import DataSelect from '../../components/form/dataSelect'
import UserSelect from '../../components/form/userSelect'
import { Candidate, Company, Device, deviceSchema } from '../../types'
import { useTalentApi } from '../../store/mainContext'
import { validateResponse } from '../../utils'

const STATUS_OPTIONS = [
  'Assigned',
  'Available',
  'Service',
  'Broken',
  'Down',
  'Missing',
  'Returned',
] as const

const DEVICE_TYPE_OPTIONS = [
  'Ipad',
  'Macbook Air',
  'Macbook Pro',
  'Notebook',
  'Ipad Mini',
] as const

const CABLE_OPTIONS = [
  'Has both',
  'Has Cable',
  'Has Charger',
  'Nothing',
] as const
const BOX_OPTIONS = ['Yes', 'No'] as const

type DeviceStatusOption = (typeof STATUS_OPTIONS)[number]
type DeviceTypeOption = (typeof DEVICE_TYPE_OPTIONS)[number]
type CableOption = (typeof CABLE_OPTIONS)[number]
type BoxOption = (typeof BOX_OPTIONS)[number]

type DeviceFormState = {
  serialNumber: string
  brand: string
  model: string
  type: DeviceTypeOption | ''
  status: DeviceStatusOption
  currentLocation: string
  companyId: string
  candidateId: string
  color: string
  generation: string
  processor: string
  year: string
  memory: string
  storage: string
  display: string
  hasBox: BoxOption
  hasCableAndCharger: CableOption
  purchasePrice: string
  boughtOn: string
  boughtBy: string
  boughtFrom: string
  comment: string
}

interface DeviceSidePanelProps {
  open: boolean
  deviceId?: string
  initialCandidateId?: string
  initialStatus?: DeviceStatusOption
  onClose(): void
  onSaved(): void
  onDeleted(): void
}

const createBlankFormState = (
  defaults?: Partial<DeviceFormState>,
): DeviceFormState => ({
  serialNumber: '',
  brand: '',
  model: '',
  type: '',
  status: 'Available',
  currentLocation: '',
  companyId: '',
  candidateId: '',
  color: '',
  generation: '',
  processor: '',
  year: '',
  memory: '',
  storage: '',
  display: '',
  hasBox: 'No',
  hasCableAndCharger: 'Nothing',
  purchasePrice: '',
  boughtOn: '',
  boughtBy: '',
  boughtFrom: '',
  comment: '',
  ...defaults,
})

const toDateInputValue = (timestamp?: number | null): string => {
  if (!timestamp && timestamp !== 0) return ''
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const fromDateInputValue = (value: string): number | null => {
  if (!value) return null
  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

const createFormStateFromDevice = (device: Device): DeviceFormState => ({
  serialNumber: device.serialNumber ?? '',
  brand: device.brand ?? '',
  model: device.model ?? '',
  type: (device.type as DeviceTypeOption | undefined) ?? '',
  status: (device.status as DeviceStatusOption | undefined) ?? 'Available',
  currentLocation: device.currentLocation ?? '',
  companyId: device.companyId ?? '',
  candidateId: device.candidateId ?? '',
  color: device.color ?? '',
  generation: device.generation ?? '',
  processor: device.processor ?? '',
  year: device.year != null ? String(device.year) : '',
  memory: device.memory ?? '',
  storage: device.storage ?? '',
  display: device.display ?? '',
  hasBox: device.hasBox ? 'Yes' : 'No',
  hasCableAndCharger:
    (device.hasCableAndCharger as CableOption | undefined) ?? 'Nothing',
  purchasePrice:
    typeof device.purchasePrice === 'number'
      ? String(device.purchasePrice)
      : '',
  boughtOn: toDateInputValue(device.boughtOn),
  boughtBy: device.boughtBy ?? '',
  boughtFrom: device.boughtFrom ?? '',
  comment: device.comment ?? '',
})

const editableFields: Array<keyof DeviceFormState> = [
  'serialNumber',
  'brand',
  'model',
  'type',
  'status',
  'currentLocation',
  'companyId',
  'candidateId',
  'color',
  'generation',
  'processor',
  'year',
  'memory',
  'storage',
  'display',
  'hasBox',
  'hasCableAndCharger',
  'purchasePrice',
  'boughtOn',
  'boughtBy',
  'boughtFrom',
  'comment',
]

const boxOptionToBoolean = (option: BoxOption): boolean => option === 'Yes'

const DeviceSidePanel = ({
  open,
  deviceId,
  initialCandidateId,
  initialStatus,
  onClose,
  onSaved,
  onDeleted,
}: DeviceSidePanelProps) => {
  const api = useTalentApi()

  const isNew = !deviceId

  const [formState, setFormState] = React.useState<DeviceFormState | null>(null)
  const [baseline, setBaseline] = React.useState<DeviceFormState | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setFormState(null)
      setBaseline(null)
      setError(null)
      setIsLoading(false)
      setIsSaving(false)
      setIsDeleting(false)
      return
    }

    if (!deviceId) {
      const blank = createBlankFormState({
        candidateId: initialCandidateId ?? '',
        status:
          initialStatus ?? (initialCandidateId ? 'Assigned' : 'Available'),
      })
      setFormState(blank)
      setBaseline(blank)
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    api
      .get(`devices/${deviceId}`)
      .then(validateResponse(deviceSchema))
      .then((device) => {
        const state = createFormStateFromDevice(device)
        setFormState(state)
        setBaseline(state)
      })
      .catch((err) => {
        console.error(err)
        setError('Unable to load device details.')
      })
      .finally(() => setIsLoading(false))
  }, [api, deviceId, initialCandidateId, initialStatus, open])

  const isDirty = React.useMemo(() => {
    if (!baseline || !formState) return false
    return editableFields.some((field) => baseline[field] !== formState[field])
  }, [baseline, formState])

  const canSave = Boolean(formState) && (isNew || isDirty)

  const handleFieldChange = <Key extends keyof DeviceFormState>(
    field: Key,
    value: DeviceFormState[Key],
  ) => {
    setFormState((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const handleStringInput =
    (field: keyof DeviceFormState) =>
    (
      _event: React.SyntheticEvent<HTMLInputElement>,
      data: InputOnChangeData,
    ) => {
      handleFieldChange(
        field,
        String(data.value ?? '') as DeviceFormState[typeof field],
      )
    }

  const handleTextAreaInput =
    (field: keyof DeviceFormState) =>
    (
      _event: React.SyntheticEvent<HTMLTextAreaElement>,
      data: TextAreaProps,
    ) => {
      handleFieldChange(
        field,
        String(data.value ?? '') as DeviceFormState[typeof field],
      )
    }

  const handleStatusChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange(
      'status',
      (data.value as DeviceStatusOption) || 'Available',
    )
  }

  const handleTypeChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange(
      'type',
      (data.value as DeviceTypeOption | undefined) ?? '',
    )
  }

  const handleCompanyChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange('companyId', (data.value as string | undefined) || '')
  }

  const handleCandidateChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange('candidateId', (data.value as string | undefined) || '')
  }

  const handleCableChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange(
      'hasCableAndCharger',
      (data.value as CableOption | undefined) ?? 'Nothing',
    )
  }

  const handleHasBoxChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange('hasBox', (data.value as BoxOption | undefined) ?? 'No')
  }

  const handleBoughtByChange = (userId: string | undefined) => {
    handleFieldChange('boughtBy', userId || '')
  }

  const handleSave = async () => {
    if (!formState) return
    setIsSaving(true)
    setError(null)

    const parsedYear = formState.year.trim()
      ? Number.parseInt(formState.year, 10)
      : null
    const parsedPrice = formState.purchasePrice.trim()
      ? Number.parseFloat(formState.purchasePrice)
      : null

    const payload: Partial<Device> = {
      serialNumber: sanitizeString(formState.serialNumber),
      brand: sanitizeString(formState.brand),
      model: sanitizeString(formState.model),
      type: formState.type || null,
      status: formState.status,
      currentLocation: sanitizeString(formState.currentLocation),
      companyId: formState.companyId || null,
      candidateId: formState.candidateId || null,
      color: sanitizeString(formState.color),
      generation: sanitizeString(formState.generation),
      processor: sanitizeString(formState.processor),
      year:
        parsedYear !== null && !Number.isNaN(parsedYear) ? parsedYear : null,
      memory: sanitizeString(formState.memory),
      storage: sanitizeString(formState.storage),
      display: sanitizeString(formState.display),
      hasBox: boxOptionToBoolean(formState.hasBox),
      hasCableAndCharger: formState.hasCableAndCharger,
      purchasePrice:
        parsedPrice !== null && !Number.isNaN(parsedPrice) ? parsedPrice : null,
      boughtOn: fromDateInputValue(formState.boughtOn),
      boughtBy: sanitizeString(formState.boughtBy),
      boughtFrom: sanitizeString(formState.boughtFrom),
      comment: sanitizeString(formState.comment),
    }

    try {
      if (isNew) {
        await api.post('devices', payload)
      } else {
        await api.patch(`devices/${deviceId}`, payload)
      }
      onSaved()
    } catch (err) {
      console.error(err)
      setError('Failed to save device changes.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deviceId) return

    const confirmed = window.confirm(
      'Are you sure you want to delete this device? This action cannot be undone.',
    )
    if (!confirmed) return

    setIsDeleting(true)
    setError(null)
    try {
      await api.delete(`devices/${deviceId}`)
      onDeleted()
    } catch (err) {
      console.error(err)
      setError('Failed to delete device.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (isSaving || isDeleting || !canSave) return
    handleSave()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/60"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <Message negative>
              <Message.Header>Something went wrong</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div className="flex justify-between">
            <h1 className="text-xl font-semibold text-slate-900">
              {isNew ? 'Add Device' : 'Edit Device'}
            </h1>
            <Button
              icon
              basic
              size="small"
              aria-label="Close device panel"
              onClick={onClose}
              disabled={isSaving || isDeleting}
            >
              <Icon name="close" />
            </Button>
          </div>
          {isLoading || !formState ? (
            <div className="flex h-full items-center justify-center py-10">
              <Loader active inline="centered" />
            </div>
          ) : (
            <Form
              onSubmit={handleSubmit}
              autoComplete="off"
              loading={isSaving}
              className="flex h-full flex-col"
            >
              <div className="flex flex-col gap-8">
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Hardware Details
                  </h2>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Input
                      label="Serial Number"
                      value={formState.serialNumber}
                      onChange={handleStringInput('serialNumber')}
                    />
                    <Form.Input
                      label="Brand"
                      value={formState.brand}
                      onChange={handleStringInput('brand')}
                    />
                  </div>
                  <Form.Input
                    label="Model"
                    value={formState.model}
                    onChange={handleStringInput('model')}
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Select
                      label="Type"
                      selection
                      clearable
                      options={DEVICE_TYPE_OPTIONS.map((type) => ({
                        key: type,
                        text: type,
                        value: type,
                      }))}
                      value={formState.type || undefined}
                      onChange={handleTypeChange}
                    />
                    <Form.Input
                      label="Color"
                      value={formState.color}
                      onChange={handleStringInput('color')}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Input
                      label="Generation"
                      value={formState.generation}
                      onChange={handleStringInput('generation')}
                    />
                    <Form.Input
                      label="Processor"
                      value={formState.processor}
                      onChange={handleStringInput('processor')}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Input
                      label="Year"
                      type="number"
                      min="0"
                      value={formState.year}
                      onChange={handleStringInput('year')}
                    />
                    <Form.Input
                      label="Memory"
                      value={formState.memory}
                      onChange={handleStringInput('memory')}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Input
                      label="Storage"
                      value={formState.storage}
                      onChange={handleStringInput('storage')}
                    />
                    <Form.Input
                      label="Display"
                      value={formState.display}
                      onChange={handleStringInput('display')}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Select
                      label="Has Box"
                      selection
                      options={BOX_OPTIONS.map((option) => ({
                        key: option,
                        text: option,
                        value: option,
                      }))}
                      value={formState.hasBox}
                      onChange={handleHasBoxChange}
                    />

                    <Form.Select
                      label="Has Cable & Charger"
                      selection
                      options={CABLE_OPTIONS.map((option) => ({
                        key: option,
                        text: option,
                        value: option,
                      }))}
                      value={formState.hasCableAndCharger}
                      onChange={handleCableChange}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Purchase Details
                  </h2>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Input
                      label="Purchase Price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formState.purchasePrice}
                      onChange={handleStringInput('purchasePrice')}
                    />
                    <Form.Input
                      label="Bought On"
                      type="date"
                      value={formState.boughtOn}
                      onChange={handleStringInput('boughtOn')}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Field>
                      <label>Bought By (User)</label>
                      <UserSelect
                        placeholder="Select user"
                        value={formState.boughtBy || ''}
                        onChange={handleBoughtByChange}
                        clerable
                        fluid
                      />
                    </Form.Field>
                    <Form.Input
                      label="Bought From"
                      value={formState.boughtFrom}
                      onChange={handleStringInput('boughtFrom')}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Assignment
                  </h2>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Select
                      label="Status"
                      selection
                      options={STATUS_OPTIONS.map((status) => ({
                        key: status,
                        text: status,
                        value: status,
                      }))}
                      value={formState.status}
                      onChange={handleStatusChange}
                    />
                    <Form.Input
                      label="Current Location"
                      value={formState.currentLocation}
                      onChange={handleStringInput('currentLocation')}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Field>
                      <label>Owner</label>
                      <DataSelect
                        selection
                        search
                        clearable
                        placeholder="Search company"
                        url="companies"
                        render={(company: Company) => company.name}
                        value={formState.companyId || undefined}
                        onChange={handleCompanyChange}
                      />
                    </Form.Field>

                    <Form.Field>
                      <label>Assigned To</label>
                      <DataSelect
                        selection
                        search
                        clearable
                        placeholder="Search candidate"
                        url="candidates"
                        render={(candidate: Candidate) => candidate.name}
                        value={formState.candidateId || undefined}
                        onChange={handleCandidateChange}
                      />
                    </Form.Field>
                  </div>
                </div>

                <div>
                  <Form.TextArea
                    label="Comment"
                    rows={4}
                    value={formState.comment}
                    placeholder="Add any relevant notes about this device"
                    onChange={handleTextAreaInput('comment')}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-slate-200 pb-10 pt-4">
                <Button
                  basic
                  onClick={onClose}
                  disabled={isSaving || isDeleting}
                >
                  <Icon name="remove" />
                  Cancel
                </Button>
                {!isNew && (
                  <Button
                    basic
                    color="red"
                    onClick={handleDelete}
                    loading={isDeleting}
                    disabled={isDeleting || isSaving}
                  >
                    <Icon name="trash" />
                    Delete
                  </Button>
                )}
                <Button
                  primary
                  type="submit"
                  loading={isSaving}
                  disabled={!canSave || isSaving || isDeleting}
                >
                  <Icon name="save" />
                  Save
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  )
}

const sanitizeString = (value: string): string | null => {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export default DeviceSidePanel
